import { useCallback, useMemo, useState } from "react";
import { checkUnitsWithStandardType } from "../Users/userDataTypes";
import {
  accountsFormDefaults,
  copyLoginUserDefaults,
  emptyIdentityForm,
  SCHEME_ID_LIST,
} from "./formDefaults";
import { getPasswordFlags, NAME_PATTERN } from "./validation";

const readEventValue = (name, event) => {
  if (!event) return null;
  if (event.toISOString) return event.toISOString();
  const target = event.target;
  if (!target) return event;
  if (target.type === "checkbox") return target.checked;
  if (target.type === "number" && target.value !== "") {
    return parseFloat(target.value);
  }
  return target.value;
};

const useIdentityForm = (logInUser) => {
  const [form, setForm] = useState(() => ({
    ...emptyIdentityForm(),
    accountId: logInUser?.accountId,
    parentId: logInUser?.id,
    ...copyLoginUserDefaults(logInUser),
  }));
  const [accountsform, setAccountsform] = useState({ ...accountsFormDefaults });
  const [fieldErrors, setFieldErrors] = useState({});
  const [passwordFlags, setPasswordFlags] = useState(getPasswordFlags());
  const [managerAccountSelect, setManagerAccountSelect] = useState("");
  const [companySelectedData, setCompanySelectedData] = useState("");
  const [citySelectedData, setCitySelectedData] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [userTypeValue, setUserTypeValue] = useState("");
  const [userDetailsNameValue, setUserDetailsNameValue] = useState("");
  const [userDetailsEmailValue, setUserDetailsEmailValue] = useState("");
  const [unlimitedLimit, setUnlimitedLimit] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [isMapModal, setIsMapModal] = useState(false);
  const [isVisableAddlocBtn, setIsVisableAddlocBtn] = useState(false);
  const [localPostionObj, setLocalPostionObj] = useState("");
  const [limitErrors, setLimitErrors] = useState({
    deviceLimitError: false,
    userLimitError: false,
    vehicleLimitError: false,
    unitsLimitsMeaasge: "",
    usersLimitsMeaasge: "",
    vehicleLimitMeaasge: "",
  });

  const patchForm = useCallback((updater) => {
    setForm((prev) =>
      typeof updater === "function" ? updater(prev) : { ...prev, ...updater },
    );
  }, []);

  const resetForm = useCallback(
    (next = {}) => {
      setForm({
        ...emptyIdentityForm(),
        accountId: logInUser?.accountId,
        parentId: logInUser?.id,
        ...copyLoginUserDefaults(logInUser),
        ...next,
      });
      setAccountsform({ ...accountsFormDefaults });
      setFieldErrors({});
      setPasswordFlags(getPasswordFlags());
      setManagerAccountSelect("");
      setCompanySelectedData("");
      setCitySelectedData("");
      setSelectedVehicle("");
      setUserTypeValue("");
      setUserDetailsNameValue("");
      setUserDetailsEmailValue("");
      setUnlimitedLimit(false);
      setPhoneError("");
    },
    [logInUser],
  );

  const handleChangeAttributes = useCallback(
    (name) => (event) => {
      let value;
      if (event.target.type === "checkbox") value = event.target.checked;
      else if (name === "c_agreedAdvancePayment") value = parseFloat(event.target.value);
      else value = event.target.value;

      setAccountsform((prev) => {
        if (name === "c_schemeId") {
          const selected = SCHEME_ID_LIST.find((scheme) => scheme.name === value);
          if (selected) return { ...prev, [name]: value, c_schemeNumber: "" };
        }
        return { ...prev, [name]: value };
      });
    },
    [],
  );

  const changeAttribute2 = useCallback((name, event) => {
    const value =
      name === "notificators"
        ? event.target.checked
          ? "mail"
          : ""
        : event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
    patchForm((prev) => {
      const attributes = { ...prev.attributes, [name]: value };
      if (name === "measurementStandard") {
        const data = checkUnitsWithStandardType(value);
        attributes.distanceUnit = data[0].distanceUnit.key;
        attributes.speedUnit = data[0].speedUnit.key;
        attributes.volumeUnit = data[0].volumeUnit.key;
      }
      return { ...prev, attributes };
    });
  }, [patchForm]);

  const handleChange = useCallback(
    (name) => (event) => {
      if (!event) {
        patchForm({ [name]: null });
        return;
      }
      if (name === "name" || name === "accountName") {
        const value = event.target.value;
        if (!NAME_PATTERN.test(value)) return;
        patchForm((prev) => ({
          ...prev,
          [name]: value,
          ...(name === "accountName" ? { name: value } : {}),
        }));
        return;
      }
      if (name === "contractType") {
        patchForm((prev) => ({ ...prev, [name]: event.target.value, amUserId: null }));
        setManagerAccountSelect("");
        return;
      }
      if (name === "status") {
        patchForm((prev) => ({ ...prev, [name]: event.target.value, creditLimit: 0 }));
        setAccountsform((prev) => ({ ...prev, c_agreedAdvancePayment: 0.0 }));
        return;
      }
      if (name === "password" || name === "confirmPassword") {
        const value = event.target.value;
        patchForm((prev) => {
          const next = { ...prev, [name]: value };
          setPasswordFlags(getPasswordFlags(next.password, next.confirmPassword));
          return next;
        });
        return;
      }
      if (name === "userType") {
        const userType = event.target.value;
        const vehicleLimit = userType === 3 ? 10000 : userType === 2 || userType === 1 ? 1000 : 0;
        setUserTypeValue(userType);
        patchForm((prev) => ({ ...prev, userType, vehicleLimit }));
        return;
      }
      const value = readEventValue(name, event);
      if (name === "accountName" && userTypeValue === 4) setUserDetailsNameValue(value);
      if (name === "accountEmail" && userTypeValue === 4) setUserDetailsEmailValue(value);
      if (name === "phone") {
        const phoneStr = String(value || "").trim();
        setPhoneError(/^\d{3,}$/.test(phoneStr) ? "" : "Enter at least 3 digits");
      }
      patchForm((prev) => ({
        ...prev,
        [name]: value,
        ...(name === "accountEmail" ? { email: value } : {}),
      }));
    },
    [patchForm, userTypeValue],
  );

  const generateToken = useCallback(() => {
    const symbols = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let token = "";
    for (let i = 0; i < 32; i += 1) {
      token += symbols.charAt(Math.floor(Math.random() * symbols.length));
    }
    patchForm({ token });
  }, [patchForm]);

  const setNoToken = useCallback(
    (event) => {
      event.stopPropagation();
      patchForm({ token: null });
    },
    [patchForm],
  );

  const changeLimit = useCallback((name, event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setUnlimitedLimit(value);
    patchForm({
      deviceLimit: value ? -1 : 0,
      vehicleLimit: value ? -1 : 0,
      userLimit: value ? -1 : 0,
    });
    if (value) {
      setLimitErrors({
        deviceLimitError: false,
        userLimitError: false,
        vehicleLimitError: false,
        unitsLimitsMeaasge: "",
        usersLimitsMeaasge: "",
        vehicleLimitMeaasge: "",
      });
    }
  }, [patchForm]);

  const handleChangeExpense = useCallback(
    (name, value) => {
      if (name === "garage") patchForm({ garage: value });
    },
    [patchForm],
  );

  const handleClickShowPostion = useCallback(() => {
    setIsMapModal(true);
    setIsVisableAddlocBtn(false);
  }, []);

  const getPostion = useCallback((latlng, zoom) => {
    if (latlng?.lat && latlng?.lng && zoom) {
      setLocalPostionObj({ latlng, zoom });
      setIsVisableAddlocBtn(true);
    }
  }, []);

  const setSelectedLocation = useCallback(() => {
    if (!localPostionObj) return;
    patchForm({
      latitude: localPostionObj.latlng.lat,
      longitude: localPostionObj.latlng.lng,
      zoom: localPostionObj.zoom,
    });
    setIsMapModal(false);
  }, [localPostionObj, patchForm]);

  const [accountSelectedData, setAccountSelectedData] = useState("");
  const [userSelectedData, setUserSelectedData] = useState("");

  const timezone_handleChange = useCallback(
    (name, value) => {
      if (name === "timezone") {
        patchForm((prev) => {
          const attributes = { ...prev.attributes };
          if (!value || value.value === "None") delete attributes[name];
          else attributes[name] = value.value;
          return { ...prev, attributes };
        });
        return;
      }
      if (name === "c_am" || name === "amUserId") {
        patchForm({ amUserId: value?.id ?? value?.value ?? null });
        setManagerAccountSelect(value);
        return;
      }
      if (name === "vehicleId") {
        setSelectedVehicle(value);
        return;
      }
      if (name === "accountId") {
        patchForm({
          accountId: value?.value,
          userId: "",
          deviceLimit: 0,
          userLimit: 0,
        });
        setAccountSelectedData(value);
        return;
      }
      if (name === "parentId") {
        patchForm({
          parentId: value?.value,
          deviceLimit: 0,
          userLimit: 0,
        });
        setUserSelectedData(value);
        return;
      }
      if (name === "companyId") {
        patchForm({ companyId: value?.id });
        setCompanySelectedData(value);
        return;
      }
      if (name === "city") {
        patchForm({ city: value?.id });
        setCitySelectedData(value);
        return;
      }
      if (name === "c_city") {
        setAccountsform((prev) => ({
          ...prev,
          [name]: value?.id,
          c_cityArabic: value?.cityAr || prev?.c_cityArabic,
        }));
        return;
      }
      patchForm({ [name]: value?.value ?? value });
    },
    [patchForm],
  );

  const timeDateOnchange = useCallback(
    (date) => patchForm({ timeDate: date }),
    [patchForm],
  );

  const setExpirationTime = useCallback(
    (event) => patchForm({ expirationTime: event }),
    [patchForm],
  );

  const formProps = useMemo(
    () => ({
      form,
      accountsform,
      addUserFieldErrors: fieldErrors,
      ...passwordFlags,
      managerAccountSelect,
      companySelectedData,
      citySelectedData,
      accountSelectedData,
      userSelectedData,
      selectedVehicle,
      userTypeValue,
      userDetailsNameValue,
      userDetailsEmailValue,
      unlimitedLimit,
      phoneError,
      isMapModal,
      isVisableAddlocBtn,
      ...limitErrors,
      handleChange,
      handleChangeAttributes,
      changeAttribute2,
      changeLimit,
      handleChangeExpense,
      generateToken,
      setNoToken,
      timezone_handleChange,
      timeDateOnchange,
      setExpirationTime,
      handleClickShowPostion,
      getPostion,
      setSelectedLocation,
      setManagerAccountSelect,
      setCompanySelectedData,
      setCitySelectedData,
      setSelectedVehicle,
      setFieldErrors,
    }),
    [
      form,
      accountsform,
      fieldErrors,
      passwordFlags,
      managerAccountSelect,
      companySelectedData,
      citySelectedData,
      accountSelectedData,
      userSelectedData,
      selectedVehicle,
      userTypeValue,
      userDetailsNameValue,
      userDetailsEmailValue,
      unlimitedLimit,
      phoneError,
      isMapModal,
      isVisableAddlocBtn,
      limitErrors,
      handleChange,
      handleChangeAttributes,
      changeAttribute2,
      changeLimit,
      handleChangeExpense,
      generateToken,
      setNoToken,
      timezone_handleChange,
      timeDateOnchange,
      setExpirationTime,
      handleClickShowPostion,
      getPostion,
      setSelectedLocation,
    ],
  );

  return {
    ...formProps,
    resetForm,
    patchForm,
    setForm,
    setAccountsform,
    setIsMapModal,
  };
};

export default useIdentityForm;
